type CompactDateParts = {
  day: string;
  fallback: string;
  month: string;
  monthFirst: boolean;
  separator: string;
};

const extractCompactDateParts = (
  date: Date,
  locale: string,
): CompactDateParts => {
  const formatter = new Intl.DateTimeFormat(locale, {
    month: "numeric",
    day: "numeric",
  });
  const parts = formatter.formatToParts(date);
  const month = parts.find((part) => part.type === "month")?.value;
  const day = parts.find((part) => part.type === "day")?.value;
  const monthIndex = parts.findIndex((part) => part.type === "month");
  const dayIndex = parts.findIndex((part) => part.type === "day");
  const separator = parts.find((part) => part.type === "literal")?.value ?? "/";
  const fallback = formatter.format(date);

  return {
    day: day ?? fallback,
    fallback,
    month: month ?? fallback,
    monthFirst:
      monthIndex !== -1 && dayIndex !== -1 ? monthIndex < dayIndex : true,
    separator,
  };
};

const formatCompactMonthDay = (date: Date, locale: string): string => {
  const { day, fallback, month, monthFirst, separator } =
    extractCompactDateParts(date, locale);

  if (month === fallback || day === fallback) {
    return fallback;
  }

  return monthFirst
    ? `${month}${separator}${day}`
    : `${day}${separator}${month}`;
};

const isSameCalendarDay = (start: Date, end: Date): boolean =>
  start.getFullYear() === end.getFullYear() &&
  start.getMonth() === end.getMonth() &&
  start.getDate() === end.getDate();

/**
 * X軸や週ラベルで使う短い日付範囲を、locale の月日順を保ったまま組み立てます。
 * `i18n.date()` が日本語で `3月2日` のような長い形を返しても、ここでは `3/2` 系へ正規化します。
 * chart の partial bucket は 1 日分でも `startOf("day")` / `endOf("day")` を渡すため、
 * 同じ calendar day なら時刻差を無視して単日ラベルへ畳みます。
 */
export function formatCompactMonthDayRange(
  start: Date,
  end: Date,
  locale: string,
): string {
  const startLabel = formatCompactMonthDay(start, locale);
  if (isSameCalendarDay(start, end)) {
    return startLabel;
  }

  const endParts = extractCompactDateParts(end, locale);
  const endLabel =
    start.getMonth() === end.getMonth() &&
    start.getFullYear() === end.getFullYear()
      ? endParts.day
      : formatCompactMonthDay(end, locale);

  return `${startLabel} - ${endLabel}`;
}
